/**
 * AI Assistant navigation directory (Phase 1).
 *
 * The routes the assistant may send a member to — powers the `navigate` tool
 * (validated server-side against this list; the member UI renders the result
 * as a button) and the scripted help fallback's deep links. Routes are copied
 * from the member SPA route table (`apps/member/src/App.tsx`); a parity test
 * in this package asserts they stay in sync. Descriptions are written FOR THE
 * MODEL — they say when to send the member there, not what the page is called.
 */

export type AssistantNavTarget = {
  /** Member-SPA route path (no origin). */
  route: string;
  /** Human label rendered on the navigation button. */
  label: string;
  /** Model-facing guidance: when this destination is the right answer. */
  description: string;
  /** Route sits behind the active-slot guard — needs an activated slot. */
  postSlot?: boolean;
  /**
   * Route is additionally feature-gated tenant-side (auto-ship, messaging,
   * subscription…). The tool still validates only against this map; a gated
   * page shows its own explanation when the feature is off.
   */
  featureGated?: boolean;
};

export const ASSISTANT_NAV_MAP: AssistantNavTarget[] = [
  {
    route: '/dashboard',
    label: 'Dashboard',
    description:
      'Home overview: wallet totals, recent earnings, announcements, and quick stats.',
  },
  {
    route: '/',
    label: 'Store',
    description:
      'The online store: browse and buy products and membership kits with member pricing.',
  },
  {
    route: '/orders',
    label: 'My Orders',
    description:
      'Order history and per-order status/tracking. Send the member here to check on a purchase.',
  },
  {
    route: '/code-vault',
    label: 'Code Vault',
    description:
      'The member’s purchased kit Code+PIN inventory. Codes here are redeemed to activate slots.',
  },
  {
    route: '/redeem',
    label: 'Redeem a Code',
    description:
      'Redeem a kit Code+PIN to activate a new slot (choose sponsor/placement here).',
    postSlot: true,
  },
  {
    route: '/genealogy',
    label: 'Genealogy',
    description:
      'Network trees: sponsor (unilevel) and placement (binary) views of the member’s team.',
    postSlot: true,
  },
  {
    route: '/invite',
    label: 'Invite Friends',
    description:
      'The member’s replicated signup links and referral codes for recruiting.',
    postSlot: true,
  },
  {
    route: '/leads',
    label: 'Leads',
    description: 'Captured signup leads from the member’s replicated links.',
    postSlot: true,
  },
  {
    route: '/earnings',
    label: 'Earnings',
    description:
      'Detailed earnings breakdown by plan and period, plus points history.',
    postSlot: true,
  },
  {
    route: '/cash-out',
    label: 'Cash Out',
    description:
      'Request a payout of the cash wallet balance and review payout history.',
    postSlot: true,
  },
  {
    route: '/wallet/top-up',
    label: 'Top Up',
    description: 'Deposit money into the wallet (when top-ups are enabled).',
    postSlot: true,
    featureGated: true,
  },
  {
    route: '/auto-ship',
    label: 'Auto-Ship',
    description:
      'Monthly auto-ship subscription management (when auto-ship is enabled).',
    postSlot: true,
    featureGated: true,
  },
  {
    route: '/messages',
    label: 'Messages',
    description:
      'Buyer↔seller chat inbox (when messaging is enabled). NOT this assistant — human sellers.',
    featureGated: true,
  },
  {
    route: '/notifications',
    label: 'Notifications',
    description: 'In-app notification feed (earnings alerts, announcements).',
  },
  {
    route: '/training-videos',
    label: 'Training',
    description: 'Training video library for members.',
    postSlot: true,
  },
  {
    route: '/subscribe',
    label: 'Subscribe',
    description:
      'Paid member subscription that unlocks live-session links (when enabled).',
    postSlot: true,
    featureGated: true,
  },
  {
    route: '/settings',
    label: 'Settings',
    description:
      'Profile, password, 2FA, and preference settings for the member’s own account.',
  },
  {
    route: '/faq',
    label: 'FAQ',
    description: 'Public frequently-asked-questions page.',
  },
  {
    route: '/track',
    label: 'Track Order',
    description: 'Public order-tracking lookup by order code.',
  },
];

/** True when `route` is a destination the assistant is allowed to link. */
export function isAssistantNavRoute(route: string): boolean {
  return ASSISTANT_NAV_MAP.some((t) => t.route === route);
}
